import { useCallback } from 'react';
import { useAtomValue } from 'jotai';

import { JobApplicationContactChannel } from '@restworld/utility-types';
import { useWorkerJobPositionsService } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';

import { selectedJobApplicationAtom } from '../../../employer-job-listing.atoms';
import { getJobApplicationId } from '../../../candidate-visibility';

/**
 * Records that the employer clicked "Call" or "WhatsApp" in the candidate modal: a PostHog event
 * and a POST to the backend. Returns a stable `recordContact(channel)` that never throws, so the
 * caller can always go on and open the tel:/wa.me link.
 */
export function useContactCandidate() {
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { trackEvent } = useAnalytics();
  const workerJobPositionsService = useWorkerJobPositionsService();

  return useCallback(
    (channel: JobApplicationContactChannel): void => {
      // Rebuilt from its parts: the kanban board overwrites `id` (see getJobApplicationId).
      const jobApplicationId = getJobApplicationId(selectedJobApplication);

      try {
        trackEvent('employer_contacts_candidate', {
          channel,
          job_application_id: jobApplicationId ?? null,
          job_position_id: selectedJobApplication?.job_position_id ?? null,
        });
      } catch {
        // Analytics must never get in the way of contacting the candidate.
      }

      if (!jobApplicationId) return;

      try {
        // Best-effort measurement: a failure must never block or alter opening the link.
        workerJobPositionsService.createJobApplicationContact(jobApplicationId, channel).catch(() => undefined);
      } catch {
        // A synchronous failure building the request is swallowed for the same reason.
      }
    },
    [selectedJobApplication, trackEvent, workerJobPositionsService]
  );
}
